import { useState } from 'react';
import {
  type EditingHintHighlight,
  type FormattingMark,
  type PlaceholderSpan,
  type PlaceholderValues,
  resolvePlaceholders,
} from '@suvoda-uat/domain';
import { MarksView } from '../formatting/MarksView';

/**
 * The per-subsection General Instructions value (F-113 / #41 / UAT-13): the
 * template prose `body` plus the reused F-049 sidecars. The card's prop shape is
 * defined by this feature (the #111 shell's bare-string prop was a placeholder).
 */
export type GeneralInstructionsValue = {
  /** The instructions prose — stored RAW, with `{…}` tokens intact (F-114). */
  readonly body: string;
  /** Template-derived inline emphasis (reused F-049 sidecar). */
  readonly formatting?: readonly FormattingMark[];
  /** Template "needs review" highlights — in-app cue only, NEVER exported. */
  readonly editingHintHighlights?: readonly EditingHintHighlight[];
  /** F-114: placeholder occurrence spans over `body`. Resolved at render. */
  readonly placeholders?: readonly PlaceholderSpan[];
};

const NO_VALUES: PlaceholderValues = new Map();

export type GeneralInstructionsProps = {
  /** The subsection's General Instructions value (body + reused F-049 sidecars). */
  instructions: GeneralInstructionsValue;
  /**
   * F-114 (#114, US3): the per-trial resolved value map (`canonicalId → value`).
   * Absent / a missing key ⇒ the placeholder renders its display name, never a raw
   * `{…}` token (FR-018). Supplying a different map re-renders the same stored
   * prose with new values (FR-019).
   */
  placeholderValues?: PlaceholderValues;
  /**
   * Commit handler for an edited body. Optional — when absent the edit is kept in
   * local state only (the demo / Storybook path), mirroring the title rename.
   */
  onSave?: (nextBody: string) => void;
};

/**
 * The "GENERAL INSTRUCTIONS" block shown above a subsection's test-case table
 * (Figma 1061-101043) and as the sole body of an instructive subsection (Figma
 * 1556-83968). The body renders through {@link MarksView} so template highlights
 * surface as the never-exported in-app "needs review" cue (US2). The body is
 * inline-editable with the SAME affordance as the test-case name: hover tints it
 * #515FFC, a tap opens a bordered editor with a focus ring + cancel (×) / save (✓).
 */
export function GeneralInstructions({
  instructions,
  placeholderValues,
  onSave,
}: GeneralInstructionsProps) {
  const { body, formatting, editingHintHighlights, placeholders } = instructions;
  const [value, setValue] = useState(body);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  // Re-sync when the source body changes (navigating between subsections) without
  // clobbering an in-progress local edit.
  const [synced, setSynced] = useState(body);
  if (body !== synced) {
    setSynced(body);
    setValue(body);
    setDraft(body);
    setEditing(false);
  }

  // FR-008 (I-GI-10): the template highlights/formatting/placeholders are anchored
  // to the ORIGINAL body; once edited they no longer align, so they are dropped
  // (never re-anchored). Marks + placeholder injection apply only while pristine.
  const pristine = value === body;

  // F-114 (FR-018/FR-020): while pristine, inject the resolved values into the raw
  // body and remap the F-049 marks onto the RENDERED string (the pure domain crux).
  // Once edited, the body is literal (tokens already rendered) and marks dropped.
  const resolved = pristine
    ? resolvePlaceholders(
        value,
        placeholders ?? [],
        formatting ?? [],
        editingHintHighlights ?? [],
        placeholderValues ?? NO_VALUES,
      )
    : { body: value, formatting: [], editingHintHighlights: [] };

  const begin = () => {
    setDraft(resolved.body); // edit the rendered text the reviewer actually sees
    setEditing(true);
  };
  const commit = () => {
    const next = draft.trim();
    if (next && next !== resolved.body) {
      setValue(next);
      onSave?.(next);
    }
    setEditing(false);
  };
  const cancel = () => {
    setDraft(resolved.body);
    setEditing(false);
  };

  // FR-003 (defensive): a blank body renders nothing.
  if (resolved.body.trim().length === 0) return null;

  return (
    <div data-test-case-instructions className="flex flex-col gap-1 px-3 py-[10px]">
      <p className="m-0 text-[11px] font-medium uppercase leading-[1.4] tracking-[-0.11px] text-black/60">
        General Instructions:
      </p>
      {editing ? (
        // Figma 1061-112518 (shared with the title editor): a bordered field with
        // a focus ring and cancel (×) / save (✓) controls — no blur-commit.
        <div className="flex items-start gap-2 rounded-[4px] border border-[#3c48ce] bg-white px-[9px] py-[6px] shadow-[0_0_0_2px_rgba(60,72,206,0.25)]">
          <textarea
            autoFocus
            value={draft}
            aria-label="Edit general instructions"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            rows={2}
            className="m-0 min-w-0 flex-1 resize-none border-0 bg-transparent p-0 text-[12px] font-normal leading-[1.4] tracking-[-0.12px] text-[#292938] outline-none"
          />
          <button
            type="button"
            aria-label="Cancel editing instructions"
            onClick={cancel}
            className="inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-[#67738e] hover:text-[#292938]"
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            aria-label="Save instructions"
            onClick={commit}
            className="inline-flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-[3px] border-0 bg-[#1d7c5f] text-white hover:brightness-110"
          >
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={begin}
          title="Edit instructions"
          className="m-0 cursor-text border-0 bg-transparent p-0 text-left text-[12px] font-normal leading-[1.4] tracking-[-0.12px] text-black/60 transition-colors hover:text-[#515ffc]"
        >
          <MarksView
            body={resolved.body}
            {...(resolved.formatting.length > 0 ? { formatting: resolved.formatting } : {})}
            {...(resolved.editingHintHighlights.length > 0
              ? { editingHintHighlights: resolved.editingHintHighlights }
              : {})}
          />
        </button>
      )}
    </div>
  );
}
